import { HelpCircle, Plus } from 'lucide-react';

import { Section } from '@/components/ui/Section';
import type { Slide } from '@/lib/schema.types';
import { useEditorActions } from '@/store/editor.store';

import { QuizBranchEditor } from './QuizBranchEditor';
import { RoutingEditor } from './RoutingEditor';

interface Props {
  slide: Slide;
}

/**
 * The adaptive half of a pack slide's properties: kind, routing, and
 * kind-specific editors (quiz first). Renders only in pack mode and only
 * for slides that carry `beatMeta`. SlideProperties is the mount point.
 */
export function AdaptivePanel({ slide }: Props) {
  const actions = useEditorActions();
  const bm = slide.beatMeta;
  if (!bm) return null;

  const isQuiz = bm.kind === 'quiz';
  const beatId = bm.beatId ?? '—';

  return (
    <>
      <Section title="Adaptive">
        <div className="flex items-center justify-between rounded-md border border-border bg-bg-subtle/40 px-2.5 py-2 text-[12px]">
          <div className="flex items-center gap-2">
            <KindBadge kind={bm.kind} />
            <span className="font-mono text-[11px] text-fg-subtle">{beatId}</span>
          </div>
          {typeof bm.estimatedDurationSec === 'number' && (
            <span className="text-[11px] text-fg-subtle">
              ~{Math.round(bm.estimatedDurationSec)}s
            </span>
          )}
        </div>
      </Section>

      <RoutingEditor slide={slide} />

      {isQuiz && <QuizBranchEditor slide={slide} />}

      {isQuiz && (
        <Section title="Quiz set">
          <button
            type="button"
            onClick={() => actions.insertQuizSlide({ afterSlideId: slide.id })}
            className="flex w-full items-center justify-center gap-1.5 rounded-md border border-dashed border-accent/40 bg-accent-soft/40 px-2 py-2 text-[12px] font-semibold text-accent transition hover:bg-accent-soft"
          >
            <Plus size={12} /> Add another question
          </button>
          <p className="mt-1.5 text-[10.5px] text-fg-subtle">
            Chains a new quiz slide after this one. Keeping one question per
            slide keeps each checkpoint clean.
          </p>
        </Section>
      )}
    </>
  );
}

function KindBadge({ kind }: { kind?: string }) {
  const k = kind ?? 'unknown';
  const palette: Record<string, string> = {
    teach: 'bg-blue-100 text-blue-700',
    quiz: 'bg-amber-100 text-amber-700',
    summary: 'bg-emerald-100 text-emerald-700',
    pace_check: 'bg-violet-100 text-violet-700',
    confidence_check: 'bg-cyan-100 text-cyan-700',
    engagement_reset: 'bg-pink-100 text-pink-700',
  };
  const cls = palette[k] ?? 'bg-bg-subtle text-fg-muted';
  return (
    <span
      className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-[0.08em] ${cls}`}
    >
      {k === 'quiz' && <HelpCircle size={10} />}
      {k}
    </span>
  );
}
